import { FontIcon } from '../FontIcon';
import { Ten } from '../SVGIcons/Ten';
import { Skeleton } from '../Skeleton';
import { LiveLabel, LiveLabelKind } from '../LiveLabel';
import styles from './poster.module.sass';
import cn from 'classnames';

interface BaseProps extends React.HTMLAttributes<HTMLDivElement> {
    isLoading?: boolean;
}

interface LoadingProps extends BaseProps {
    isLoading: true;
    picture?: string; // Optional when loading
    width?: number;
    height?: number;
    alt?: string;
    inline?: boolean;
    showPlayButton?: boolean;
    vertical?: boolean;
    badge?: number | string;
    label?: LiveLabelKind;
    labelText?: string;
}

interface LoadedProps extends BaseProps {
    isLoading?: false;
    picture: string; // Required when not loading
    width?: number;
    height?: number;
    alt?: string;
    inline?: boolean;
    showPlayButton?: boolean;
    /** Figma "Cards3.0/Vertical Poster" */
    vertical?: boolean;
    badge?: number | string;
    label?: LiveLabelKind;
    labelText?: string;
}

type PosterProps = LoadingProps | LoadedProps;

export const Poster = ({
    picture,
    width,
    height,
    alt,
    inline,
    isLoading,
    showPlayButton,
    vertical,
    badge,
    label,
    labelText,
    ...props
}: PosterProps) => {
    const sizeVars = {
        ...(width ? { '--poster-w': `${width / 16}rem` } : null),
        ...(height ? { '--poster-h': `${height / 16}rem` } : null),
    } as React.CSSProperties;

    if (isLoading) {
        return (
            <div
                {...props}
                className={cn(styles.poster, inline && styles.inline, vertical && styles.vertical, props.className)}
                style={{ ...props.style, ...sizeVars }}
            >
                <Skeleton absolute />
            </div>
        );
    }

    return (
        <div
            {...props}
            className={cn(
                styles.poster,
                inline && styles.inline,
                vertical && styles.vertical,
                props.className,
                props.onMouseEnter && styles.hover
            )}
            style={{ ...props.style, ...sizeVars }}
        >
            <img
                className={styles.picture}
                src={picture}
                alt={alt ? alt : ''}
                width={width ? width : undefined}
                height={height ? height : undefined}
            />
            {badge != null && badge !== '' && (
                <span className={styles.badge}>
                    <span className={styles['badge-card']}>{badge}</span>
                    <Ten />
                </span>
            )}
            {label && <LiveLabel kind={label} text={labelText} />}
            {showPlayButton && <FontIcon name="play-circle" size="size60" md="x-large" className={styles.play} />}
        </div>
    );
};
